import { type V2, angleDiff, angleTo, dirFromAngle, dist, rotate } from '../core/math';

/**
 * Area shapes used by skills, boss telegraphs and ground effects. Every shape
 * is placed relative to an origin and a facing angle so the same definition
 * works for any caster orientation.
 */
export type Shape =
  /** Disc centred `offset` units in front of the origin. */
  | { kind: 'circle'; radius: number; offset?: number }
  /** Pie slice from the origin; `angle` is the full opening angle in radians. */
  | { kind: 'cone'; radius: number; angle: number; rotation?: number }
  /** Oriented box starting `offset` in front of the origin and extending `length` forward. */
  | { kind: 'rect'; length: number; width: number; offset?: number; rotation?: number }
  /** Donut: hits between inner and outer radius (safe spot in the middle). */
  | { kind: 'ring'; inner: number; outer: number; offset?: number };

export interface Placement {
  origin: V2;
  facing: number;
}

/** Centre point of the shape in world space (for VFX and telegraph placement). */
export function shapeCenter(shape: Shape, p: Placement): V2 {
  const f = dirFromAngle(p.facing);
  switch (shape.kind) {
    case 'circle':
    case 'ring': {
      const o = shape.offset ?? 0;
      return { x: p.origin.x + f.x * o, z: p.origin.z + f.z * o };
    }
    case 'cone':
      return { ...p.origin };
    case 'rect': {
      const d = dirFromAngle(p.facing + (shape.rotation ?? 0));
      const o = (shape.offset ?? 0) + shape.length / 2;
      return { x: p.origin.x + d.x * o, z: p.origin.z + d.z * o };
    }
  }
}

/**
 * True if a target circle (centre `pos`, radius `r`) overlaps the shape.
 * Uses slightly generous approximations (targets count as hit when any part
 * of their body is inside), which feels right for action combat.
 */
export function shapeHits(shape: Shape, p: Placement, pos: V2, r = 0): boolean {
  switch (shape.kind) {
    case 'circle': {
      const c = shapeCenter(shape, p);
      return dist(c, pos) <= shape.radius + r;
    }
    case 'ring': {
      const c = shapeCenter(shape, p);
      const d = dist(c, pos);
      return d + r > shape.inner && d - r <= shape.outer;
    }
    case 'cone': {
      const d = dist(p.origin, pos);
      if (d > shape.radius + r) return false;
      if (d <= r) return true;
      const half = shape.angle / 2;
      const dir = p.facing + (shape.rotation ?? 0);
      const off = Math.abs(angleDiff(dir, angleTo(p.origin, pos)));
      // Widen by the angular size of the target so edge-grazes count.
      const slack = Math.asin(Math.min(1, r / d));
      return off <= half + slack;
    }
    case 'rect': {
      const local = toLocal(pos, p.origin, p.facing + (shape.rotation ?? 0));
      const start = shape.offset ?? 0;
      return (
        local.z >= start - r &&
        local.z <= start + shape.length + r &&
        Math.abs(local.x) <= shape.width / 2 + r
      );
    }
  }
}

/** Transforms a world point into the frame where `facing` points along +Z. */
export function toLocal(pos: V2, origin: V2, facing: number): V2 {
  return rotate({ x: pos.x - origin.x, z: pos.z - origin.z }, -facing);
}

/** Rough bounding radius of a shape around its placement origin (for broad-phase queries). */
export function shapeReach(shape: Shape): number {
  switch (shape.kind) {
    case 'circle':
      return (shape.offset ?? 0) + shape.radius;
    case 'ring':
      return (shape.offset ?? 0) + shape.outer;
    case 'cone':
      return shape.radius;
    case 'rect':
      return Math.hypot((shape.offset ?? 0) + shape.length, shape.width / 2);
  }
}

/** Scales every linear dimension of a shape (used by "+X% area" tripods). */
export function scaleShape(shape: Shape, k: number): Shape {
  switch (shape.kind) {
    case 'circle':
      return { ...shape, radius: shape.radius * k };
    case 'ring':
      return { ...shape, inner: shape.inner * k, outer: shape.outer * k };
    case 'cone':
      return { ...shape, radius: shape.radius * k };
    case 'rect':
      return { ...shape, length: shape.length * k, width: shape.width * k };
  }
}
